import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import ManageCategoriesModal from '../ManageCategoriesModal';
import ChecklistModal from '../ChecklistModal';
import {
  GET_CHECKLIST_CATEGORIES,
  CREATE_CHECKLIST_CATEGORY,
  UPDATE_CHECKLIST_CATEGORY,
  DELETE_CHECKLIST_CATEGORY,
  CREATE_CHECKLIST,
  UPDATE_CHECKLIST,
  DELETE_CHECKLIST,
  GET_CHECKLISTS,
  DELETE_CHECKLIST_ITEM
} from '../../graphql/checklist';

function ChecklistSection() {
  const [isManageCategoriesModalOpen, setIsManageCategoriesModalOpen] = useState(false);
  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [editingChecklistCategory, setEditingChecklistCategory] = useState(null);

  // Queries
  const { 
    loading: checklistLoading, 
    error: checklistError, 
    data: checklistData, 
    refetch: refetchChecklists 
  } = useQuery(GET_CHECKLISTS);

  const { 
    loading: categoriesLoading, 
    error: categoriesError, 
    data: categoriesData, 
    refetch: refetchCategories 
  } = useQuery(GET_CHECKLIST_CATEGORIES);

  // Mutations
  const [createChecklistCategory] = useMutation(CREATE_CHECKLIST_CATEGORY, {
    onCompleted: () => {
      setIsManageCategoriesModalOpen(false);
      refetchChecklists();
      toast.success('Category created successfully');
    }
  });

  const [updateChecklistCategory] = useMutation(UPDATE_CHECKLIST_CATEGORY, {
    onCompleted: () => {
      setEditingChecklistCategory(null);
      refetchChecklists();
      toast.success('Category updated successfully');
    }
  });

  const [deleteCategory] = useMutation(DELETE_CHECKLIST_CATEGORY, {
    onCompleted: () => {
      refetchChecklists();
      toast.success('Category deleted successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete category');
    },
  });

  const [deleteChecklistItem] = useMutation(DELETE_CHECKLIST_ITEM, {
    onCompleted: () => {
      refetchChecklists();
    }
  });

  const [createChecklist] = useMutation(CREATE_CHECKLIST, {
    onCompleted: (data) => {
      refetchChecklists();
      toast.success('Checklist created successfully');
    },
    onError: (error) => {
      console.error('Create checklist error:', error);
      toast.error(error.message || 'Failed to create checklist');
    },
    update: (cache, { data: { createChecklist } }) => {
      // Update the cache to include the new checklist
      const existingData = cache.readQuery({ query: GET_CHECKLISTS });
      if (existingData) {
        cache.writeQuery({
          query: GET_CHECKLISTS,
          data: {
            checklists: [...existingData.checklists, createChecklist]
          }
        });
      }
    }
  });

  const [updateChecklist] = useMutation(UPDATE_CHECKLIST, {
    onCompleted: (data) => {
      refetchChecklists();
      toast.success('Checklist updated successfully');
    },
    onError: (error) => {
      console.error('Update checklist error:', error);
      toast.error(error.message || 'Failed to update checklist');
    },
    update: (cache, { data: { updateChecklist } }) => {
      // Update the cache with the updated checklist
      const existingData = cache.readQuery({ query: GET_CHECKLISTS });
      if (existingData) {
        const updatedChecklists = existingData.checklists.map(checklist =>
          checklist.id === updateChecklist.id ? updateChecklist : checklist
        );
        cache.writeQuery({
          query: GET_CHECKLISTS,
          data: {
            checklists: updatedChecklists
          }
        });
      }
    }
  });

  const [deleteChecklist] = useMutation(DELETE_CHECKLIST, {
    onCompleted: (data) => {
      refetchChecklists();
      toast.success('Checklist deleted successfully');
    },
    onError: (error) => {
      console.error('Delete checklist error:', error);
      console.error('Error details:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError
      });
      toast.error(error.message || 'Failed to delete checklist');
    }
  });

  // Handlers
  const handleCreateCategory = async (formData) => {
    try {
      const variables = {
        title: formData.title,
        description: formData.title, // Use title as description since it's required
        color: '#E5E7EB', // Default gray color
        sortOrder: 0.0
      };
      
      await createChecklistCategory({
        variables,
        update: (cache, { data: { createChecklistCategory } }) => {
          const existingData = cache.readQuery({
            query: GET_CHECKLIST_CATEGORIES
          });

          if (existingData) {
            // Ensure the new category has an empty items array
            const newCategory = {
              ...createChecklistCategory,
              items: [], // Initialize with empty array
              __typename: 'ChecklistCategory'
            };

            cache.writeQuery({
              query: GET_CHECKLIST_CATEGORIES,
              data: {
                checklistCategories: [
                  ...existingData.checklistCategories,
                  newCategory
                ]
              }
            });
          }
        }
      });
    } catch (error) {
      console.error('Failed to create category:', error);
      toast.error(error.message || 'Failed to create category');
    }
  };

  const handleUpdateCategory = async (formData) => {
    try {
      await updateChecklistCategory({
        variables: {
          id: editingChecklistCategory.id,
          title: formData.title,
          description: '',  // Required field, provide empty string
          color: '#FFFFFF',  // Required field, provide default color
          sortOrder: editingChecklistCategory?.sortOrder || 0.0  // Float type for sort order
        }
      });
    } catch (error) {
      console.error('Failed to update category:', error);
      throw error;
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await deleteCategory({
        variables: { id: categoryId },
      });
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleSaveChecklist = async (data) => {
    try {
      
      if (data.id) {
        // Update existing checklist
        await updateChecklist({
          variables: {
            input: {
              id: data.id,
              title: data.title,
              description: data.description || '',
              newItems: (data.newItems || []).map(item => ({
                title: item.title,
                description: item.description || '',
                categoryId: item.categoryId,
                sortOrder: item.sortOrder || 0,
                type: item.type || 'TEXT',
                isRequired: item.isRequired ?? true
              })),
              updatedItems: (data.updatedItems || []).map(item => ({
                id: item.id,
                title: item.title,
                description: item.description || '',
                categoryId: item.categoryId,
                sortOrder: item.sortOrder || 0,
                type: item.type || 'TEXT',
                isRequired: item.isRequired ?? true
              })),
              deletedItemIds: data.deletedItemIds || []
            }
          }
        });
      } else {
        // Create new checklist
        const result = await createChecklist({
          variables: {
            input: {
              title: data.title,
              description: data.description || '',
              items: (data.items || []).map(item => ({
                title: item.title,
                description: item.description || '',
                categoryId: item.categoryId,
                sortOrder: item.sortOrder || 0,
                type: item.type || 'TEXT',
                isRequired: item.isRequired ?? true
              }))
            }
          }
        });

        // Update the editing checklist with the new ID for auto-save
        if (result.data?.createChecklist?.id) {
          setEditingChecklist(prev => ({
            ...prev,
            id: result.data.createChecklist.id
          }));
        }
      }

      setIsChecklistModalOpen(false);
      setEditingChecklist(null);
    } catch (error) {
      console.error('Error saving checklist:', error);
      toast.error(error.message || 'Failed to save checklist');
    }
  };

  const handleDeleteChecklist = async (checklistId) => {
    try {
      await deleteChecklist({
        variables: { id: checklistId },
        update: (cache) => {
          // Remove the checklist from the cache
          cache.evict({ id: `Checklist:${checklistId}` });
          cache.gc();
        }
      });
    } catch (error) {
      console.error('Error deleting checklist:', error);
      toast.error('Failed to delete checklist');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingItem) return;
    try {
      
      // Call the deleteChecklist mutation with the checklist ID
      await deleteChecklist({
        variables: { id: deletingItem.id },
        update: (cache) => {
          // Remove the checklist from the cache
          cache.evict({ id: `Checklist:${deletingItem.id}` });
          cache.gc();
        }
      });
      
      setIsDeleteConfirmOpen(false);
      setDeletingItem(null);
    } catch (error) {
      console.error('Error deleting checklist:', error);
      toast.error('Failed to delete checklist');
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900">Checklist Management</h2>
        <div className="space-x-2">
          <button
            onClick={() => {
              setEditingChecklist({
                categoryId: null,
                items: []
              });
              setIsChecklistModalOpen(true);
            }}
            className="px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
          >
            Add New Checklist
          </button>
          <button
            onClick={() => setIsManageCategoriesModalOpen(true)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Manage Categories
          </button>
        </div>
      </div>

      {checklistLoading ? (
        <div>Loading checklists...</div>
      ) : checklistError ? (
        <div className="text-red-600">Error loading checklists: {checklistError.message}</div>
      ) : (
        <div className="space-y-4">
          {checklistData?.checklists.map(checklist => (
            <div key={checklist.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
              <div className="flex items-center space-x-3">
                <h3 className="text-base font-medium text-gray-900">{checklist.title}</h3>
                <span className="text-sm text-gray-500">
                  ({checklist.items.length} items in {new Set(checklist.items.map(item => item.categoryId)).size} categories)
                </span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setEditingChecklist({
                      id: checklist.id,
                      title: checklist.title,
                      items: checklist.items.map(item => ({
                        id: item.id,
                        title: item.title,
                        categoryId: item.categoryId,
                        type: item.type || 'TEXT',
                        isRequired: item.isRequired ?? true,
                        sortOrder: item.sortOrder || 0
                      }))
                    });
                    setIsChecklistModalOpen(true);
                  }}
                  className="px-2.5 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Edit List
                </button>
                <button
                  onClick={() => {
                    setDeletingItem({ id: checklist.id, title: checklist.title });
                    setIsDeleteConfirmOpen(true);
                  }}
                  className="px-2.5 py-1.5 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {isManageCategoriesModalOpen && (
        <ManageCategoriesModal
          isOpen={isManageCategoriesModalOpen}
          onClose={() => setIsManageCategoriesModalOpen(false)}
          onCreateCategory={handleCreateCategory}
          onDeleteCategory={handleDeleteCategory}
          categories={categoriesData?.checklistCategories || []}
        />
      )}

      {isChecklistModalOpen && (
        <ChecklistModal
          isOpen={isChecklistModalOpen}
          onClose={() => {
            setIsChecklistModalOpen(false);
            setEditingChecklist(null);
          }}
          onSave={handleSaveChecklist}
          onDelete={handleDeleteChecklist}
          checklist={editingChecklist}
          categories={categoriesData?.checklistCategories || []}
        />
      )}

      {isDeleteConfirmOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity">
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold leading-6 text-gray-900">
                      Delete Checklist
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete the "{deletingItem?.title}" checklist? This will delete all items in this checklist and cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                    onClick={handleDeleteConfirm}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={() => {
                      setIsDeleteConfirmOpen(false);
                      setDeletingItem(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChecklistSection; 